import { useState, useEffect, useRef, useMemo, useCallback, type KeyboardEvent as ReactKeyboardEvent } from 'react'
import { File, Clock, Search, X } from 'lucide-react'
import { useFileSystem } from '../hooks/useWorkspace'
import './QuickOpen.css'

interface QuickOpenResult {
    path: string
    name: string
    score?: number
    isRecent: boolean
}

interface QuickOpenProps {
    recentPaths?: string[]
    onSelect: (path: string) => void
    onClose: () => void
}

/**
 * Quick-open modal (Cmd+P).
 * Shows recent files + fuzzy filename search.
 */
export default function QuickOpen({ recentPaths = [], onSelect, onClose }: QuickOpenProps) {
    const { files } = useFileSystem()
    const [query, setQuery] = useState('')
    const [selectedIndex, setSelectedIndex] = useState(0)
    const inputRef = useRef<HTMLInputElement>(null)

    useEffect(() => {
        inputRef.current?.focus()
    }, [])

    // Fuzzy match: simple substring match with scoring
    const results = useMemo((): QuickOpenResult[] => {
        if (!query.trim()) {
            // Show recent files first, then all files
            const recent: QuickOpenResult[] = recentPaths
                .filter(p => files.some(f => f.path === p))
                .map(p => ({ path: p, name: p.split('/').pop() ?? p, isRecent: true }))
            const rest: QuickOpenResult[] = files
                .filter(f => !recentPaths.includes(f.path))
                .map(f => ({ path: f.path, name: f.path.split('/').pop() ?? f.path, isRecent: false }))
            return [...recent.slice(0, 5), ...rest]
        }

        const q = query.toLowerCase()
        return files
            .map((f): QuickOpenResult | null => {
                const name = (f.path.split('/').pop() ?? '').toLowerCase()
                const pathLower = f.path.toLowerCase()
                let score = 0
                // Exact filename match
                if (name === q) score = 100
                // Filename starts with query
                else if (name.startsWith(q)) score = 80
                // Filename contains query
                else if (name.includes(q)) score = 60
                // Path contains query
                else if (pathLower.includes(q)) score = 40
                else return null
                return { path: f.path, name: f.path.split('/').pop() ?? f.path, score, isRecent: false }
            })
            .filter((r): r is QuickOpenResult => r !== null)
            .sort((a, b) => (b.score ?? 0) - (a.score ?? 0))
    }, [query, files, recentPaths])

    // Reset selection when results change
    useEffect(() => {
        setSelectedIndex(0)
    }, [results.length, query])

    const handleKeyDown = useCallback((e: ReactKeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'ArrowDown') {
            e.preventDefault()
            setSelectedIndex(i => Math.min(i + 1, results.length - 1))
        } else if (e.key === 'ArrowUp') {
            e.preventDefault()
            setSelectedIndex(i => Math.max(i - 1, 0))
        } else if (e.key === 'Enter') {
            e.preventDefault()
            if (results[selectedIndex]) {
                onSelect(results[selectedIndex].path)
                onClose()
            }
        } else if (e.key === 'Escape') {
            onClose()
        }
    }, [results, selectedIndex, onSelect, onClose])

    return (
        <div className="quickopen-backdrop" onClick={onClose}>
            <div className="quickopen-modal" onClick={e => e.stopPropagation()} role="dialog">
                <div className="quickopen-input-row">
                    <Search size={16} className="quickopen-search-icon" />
                    <input
                        ref={inputRef}
                        className="quickopen-input"
                        type="text"
                        value={query}
                        onChange={e => setQuery(e.target.value)}
                        onKeyDown={handleKeyDown}
                        placeholder="Search file…"
                        autoComplete="off"
                        spellCheck="false"
                    />
                    <button
                        className="btn btn-icon quickopen-close-btn"
                        onClick={onClose}
                        title="Close (Esc)"
                        aria-label="Close"
                    >
                        <X size={16} />
                    </button>
                </div>
                <div className="quickopen-results">
                    {results.length === 0 && (
                        <div className="quickopen-empty">Inga matchningar</div>
                    )}
                    {results.slice(0, 15).map((item, i) => (
                        <button
                            key={item.path}
                            className={`quickopen-item ${i === selectedIndex ? 'selected' : ''}`}
                            onClick={() => { onSelect(item.path); onClose() }}
                            onMouseEnter={() => setSelectedIndex(i)}
                        >
                            {item.isRecent ? <Clock size={14} className="quickopen-icon recent" /> : <File size={14} className="quickopen-icon" />}
                            <span className="quickopen-name">{item.name}</span>
                            <span className="quickopen-path">{item.path}</span>
                        </button>
                    ))}
                </div>
                <div className="quickopen-footer">
                    <kbd>↑↓</kbd> navigate <kbd>↵</kbd> open <kbd>esc</kbd> close
                </div>
            </div>
        </div>
    )
}
